// Application logger. Public surface: `logger.debug/info/warn/error` for
// quick log calls, `logger.child(scope)` to namespace a chunk of logs.
//
// Level filter is set from `VITE_LOG_LEVEL` (overrides the per-env default).
// Context is masked (PII + auth secrets → '[REDACTED]') before any transport.
// The console transport is always registered eagerly; the Sentry transport is
// registered from main.ts after Sentry is initialised via `registerTransport`.

import { config } from '@core/config'
import { maskSensitiveFields } from './maskSensitiveFields'
import { consoleTransport } from './transports'
import { getSessionId, getUserId } from './identity'
import { parseOriginFromStack } from './stackTrace'
import {
  LEVEL_PRIORITY,
  type LogContext,
  type LogLevel,
  type LogRecord,
  type Transport,
} from './types'

const SERVICE_NAME = config.app.name
const SERVICE_VERSION = config.app.version

let currentMinPriority: number =
  config.logLevel === 'silent' ? Number.POSITIVE_INFINITY : LEVEL_PRIORITY[config.logLevel]
const transports: Transport[] = []

interface DispatchArgs {
  level: LogLevel
  scope: string | undefined
  message: string
  context?: LogContext
  error?: unknown
}

const buildEnrichment = (level: LogLevel, error: unknown): LogContext => {
  const enrichment: LogContext = {
    serviceName: SERVICE_NAME,
    serviceVersion: SERVICE_VERSION,
    sessionId: getSessionId(),
  }
  const uid = getUserId()
  if (uid) enrichment.userId = uid

  if (level === 'error' && error instanceof Error) {
    const origin = parseOriginFromStack(error.stack)
    if (origin) enrichment.origin = origin
  }

  return enrichment
}

const dispatch = ({ level, scope, message, context, error }: DispatchArgs) => {
  if (LEVEL_PRIORITY[level] < currentMinPriority) return

  const enrichment = buildEnrichment(level, error)
  const merged: LogContext = context ? { ...enrichment, ...context } : enrichment

  const record: LogRecord = {
    level,
    scope,
    message,
    context: maskSensitiveFields(merged),
    error,
    timestamp: new Date().toISOString(),
  }
  for (const transport of transports) transport.write(record)
}

const make = (scope?: string) => ({
  debug(msg: string, context?: LogContext) {
    dispatch({ level: 'debug', scope, message: msg, context })
  },
  info(msg: string, context?: LogContext) {
    dispatch({ level: 'info', scope, message: msg, context })
  },
  warn(msg: string, context?: LogContext) {
    dispatch({ level: 'warn', scope, message: msg, context })
  },
  error(msg: string, error?: unknown, context?: LogContext) {
    dispatch({ level: 'error', scope, message: msg, context, error })
  },
  child(childScope: string) {
    return make(scope ? `${scope}:${childScope}` : childScope)
  },
})

export const logger = make()
export type AppLogger = ReturnType<typeof make>

export const registerTransport = (transport: Transport): void => {
  if (transports.some((existing) => existing.name === transport.name)) return
  transports.push(transport)
}

export const setLogLevel = (level: LogLevel | 'silent'): void => {
  currentMinPriority = level === 'silent' ? Number.POSITIVE_INFINITY : LEVEL_PRIORITY[level]
}

// Console transport is always on so any log call before app bootstrap still
// surfaces somewhere. Sentry transport is registered after Sentry is init'd.
registerTransport(consoleTransport)
